"use client";

import React from "react";
import Image from "next/image";
import styles from "./ResponsiveImage.module.css";

export interface ImageSource {
  src: string;
  width: number;
  height: number;
}

interface ResponsiveImageProps {
  desktop?: ImageSource | null;
  tablet?: ImageSource | null;
  mobile?: ImageSource | null;
  alt: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
  /** Encode quality forwarded to <Image>. Must be in `images.qualities`
   *  (next.config.ts). Defaults to Next's 75 when unset. */
  quality?: number;
}

/**
 * Renders a CMS-sourced image at the correct intrinsic aspect ratio.
 * Next.js <Image> uses `width` / `height` to compute the aspect ratio
 * applied when CSS sets `height: auto`, so we must forward the *real*
 * Cloudinary dimensions, not a hardcoded 3:2 fallback (the previous
 * implementation forced every image into 1200×800).
 */
const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  desktop,
  tablet,
  mobile,
  alt,
  className,
  sizes,
  priority,
  quality,
}) => {
  const mobileImage = mobile || tablet || desktop;
  const desktopImage = desktop || tablet || mobile;

  if (!mobileImage && !desktopImage) return null;

  // Same image at every breakpoint → render once.
  if (
    !mobileImage ||
    !desktopImage ||
    mobileImage.src === desktopImage.src
  ) {
    const img = mobileImage || desktopImage;
    if (!img) return null;
    return (
      <Image
        src={img.src}
        alt={alt}
        width={img.width}
        height={img.height}
        className={className}
        sizes={sizes}
        priority={priority}
        // Next 16's `priority` emits the preload link but does NOT set
        // fetchpriority on it — pass it explicitly so the LCP preload is
        // requested at high priority (Lighthouse "LCP request discovery").
        fetchPriority={priority ? "high" : undefined}
        quality={quality}
      />
    );
  }

  // Different images per breakpoint: render both with visibility toggling.
  // Only the MOBILE image carries `priority` — mobile is the LCP-critical /
  // slow-network case. Marking BOTH priority made Next preload both (the
  // hidden desktop one too), splitting the connection budget and delaying the
  // visible image. The desktop image is lazy: it's `display:none` on mobile
  // (so never fetched there) and above-the-fold on desktop (so a lazy image in
  // the initial viewport still loads promptly) — desktop already scores ~95.
  return (
    <>
      <Image
        src={mobileImage.src}
        alt={alt}
        width={mobileImage.width}
        height={mobileImage.height}
        className={`${className || ""} ${styles.mobileOnly}`.trim()}
        sizes={sizes}
        priority={priority}
        // See note above: `priority` alone leaves the preload without
        // fetchpriority. The mobile image is the LCP on phones, so flag it high.
        fetchPriority={priority ? "high" : undefined}
        quality={quality}
      />
      <Image
        src={desktopImage.src}
        alt={alt}
        width={desktopImage.width}
        height={desktopImage.height}
        className={`${className || ""} ${styles.desktopOnly}`.trim()}
        sizes={sizes}
        priority={false}
        quality={quality}
      />
    </>
  );
};

export default ResponsiveImage;
